import { useState, useMemo, useEffect } from "react";
import { Box, Typography, Grid, Autocomplete, TextField, Card, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import DownloadFile from "../../components/downloadMediaFiles/downloadMediaFile";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CircularLoader from "../../components/loaders/circularLoader";
import { QR, QrActionButton } from "../qrCode/components/qrUi";

// Label above the filter box (matches the shared field kit)
const filterLabelSx = {
    display: "block",
    mb: 0.9,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#4b5563",
};

// Rounded 42px filter box with green focus ring
const filterInputSx = {
    "& .MuiOutlinedInput-root": {
        minHeight: 42,
        borderRadius: "10px",
        bgcolor: "#fff",
        fontSize: 14,
        transition: "box-shadow .15s ease",
        "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e2e8f0" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
        "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(25,135,84,0.14)" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.main },
        "&.Mui-disabled": { bgcolor: "#f8fafc" },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#eef2f6" },
    },
};

// Icon / colour / label per file type (presentation only)
const getFileMeta = (type = "") => {
    if (type.startsWith("image/")) return { label: "Image", color: QR.blue, icon: <ImageOutlinedIcon sx={{ fontSize: 16 }} /> };
    if (type.startsWith("video/")) return { label: "Video", color: QR.purple, icon: <VideocamOutlinedIcon sx={{ fontSize: 16 }} /> };
    if (type === "application/pdf") return { label: "PDF", color: "#dc2626", icon: <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} /> };
    return { label: "File", color: QR.slate, icon: <InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} /> };
};

export default function ViewUserMediaFileDetails({ show }) {
    const { getUserProfile } = useSelector((state) => state.contacts);
    const files = Array.isArray(getUserProfile?.filesdata) ? getUserProfile.filesdata : [];

    // Unique categories
    const categoryData = useMemo(
        () =>
            files
                .map((item) => ({ value: item.file_categroy_id, label: item.file_category_name }))
                .filter((obj, index, self) => index === self.findIndex((t) => t.value === obj.value)),
        [files]
    );


    // File type options
    const fileTypes = [
        { label: "All", value: 0 },
        { label: "Images", value: 1 },
        { label: "Videos", value: 2 },
        { label: "Documents", value: 3 },
    ];

    const [selectedType, setSelectedType] = useState(fileTypes[0]);
    const [selectedCategory, setSelectedCategory] = useState(categoryData[0] || null);
    const [isLoading, setIsLoading] = useState(false);


    // Filter files based on selected category and type
    const filteredFiles = useMemo(() => {
        return files.filter((file) => {
            // Category filter
            if (selectedCategory && file.file_categroy_id !== selectedCategory.value) return false;

            // File type filter
            if (selectedType.value === 1) return file.file_type.startsWith("image/");
            if (selectedType.value === 2) return file.file_type.startsWith("video/");
            if (selectedType.value === 3) return file.file_type === "application/pdf";
            return true; // All
        });
    }, [files, selectedCategory, selectedType]);

    useEffect(() => {
        if (!show) {
            setSelectedType(fileTypes[0]);
            setSelectedCategory(null);
            setIsLoading(false);
        }
    }, [show]);

    const handleMediaLoad = () => {
        setIsLoading(true);
        const timer = setTimeout(() => setIsLoading(false), 800);
        return () => clearTimeout(timer);
    };

    return (
        <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
            {/* Filters */}
            <Box
                sx={{
                    mb: 2.5,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 3,
                    border: `1px solid ${QR.border}`,
                    bgcolor: "#fff",
                    boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                }}
            >
                <Grid container columnSpacing={2.5} rowSpacing={2}>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography component="label" sx={filterLabelSx}>Category</Typography>
                        <Autocomplete
                            size="small"
                            options={categoryData}
                            value={selectedCategory}
                            disableClearable
                            onChange={(event, newValue) => { setSelectedCategory(newValue); handleMediaLoad(); setSelectedType(fileTypes[0]); }}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Select Category" sx={filterInputSx} />}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography component="label" sx={filterLabelSx}>File Type</Typography>
                        <Autocomplete
                            size="small"
                            options={categoryData.length > 0 ? fileTypes : []}
                            value={selectedType}
                            disableClearable
                            onChange={(event, newValue) => setSelectedType(newValue)}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Select File Type" sx={filterInputSx} />}
                            disabled={categoryData.length > 0 ? false : true}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box>
                {
                    isLoading === true ? <CircularLoader /> : (
                        <>
                            {filteredFiles.length === 0 ? (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    spacing={1.25}
                                    sx={{
                                        py: { xs: 5, sm: 7 },
                                        px: 2,
                                        textAlign: "center",
                                        borderRadius: 3,
                                        border: `1.5px dashed ${QR.border}`,
                                        bgcolor: "#fafbfc",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 3,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: QR.main,
                                            bgcolor: `${QR.main}14`,
                                        }}
                                    >
                                        <FolderOpenOutlinedIcon sx={{ fontSize: 30 }} />
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, color: "#334155", fontSize: 15 }}>No files found.</Typography>
                                    <Typography sx={{ color: QR.slate, fontSize: 13 }}>
                                        Try a different category or file type.
                                    </Typography>
                                </Stack>
                            ) : (
                                <Grid container spacing={2}>
                                    {filteredFiles.slice(0, 6).map((file, index) => {
                                        const meta = getFileMeta(file.file_type);
                                        return (
                                            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        height: "100%",
                                                        width: "100%",
                                                        borderRadius: 3,
                                                        border: `1px solid ${QR.border}`,
                                                        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                                                        overflow: "hidden",
                                                        transition: "box-shadow .15s ease, transform .15s ease",
                                                        "&:hover": { boxShadow: "0 8px 20px -10px rgba(16,24,40,0.25)", transform: "translateY(-1px)" },
                                                    }}
                                                >
                                                    {/* Preview */}
                                                    <Box
                                                        sx={{
                                                            height: { xs: 200, sm: 220 },
                                                            bgcolor: "#f8fafc",
                                                            borderBottom: `1px solid ${QR.border}`,
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        {file.file_url ? (
                                                            <>
                                                                {file.file_type.startsWith("image/") && (
                                                                    <img
                                                                        src={file.file_url}
                                                                        alt={file.file_category_name}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                        }}
                                                                    />
                                                                )}

                                                                {file.file_type.startsWith("video/") && (
                                                                    <video
                                                                        src={file.file_url}
                                                                        controls
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                            backgroundColor: "#0f172a",
                                                                        }}
                                                                    />
                                                                )}

                                                                {file.file_type === "application/pdf" && (
                                                                    <iframe
                                                                        // src={file?.file_url}
                                                                        src={`https://docs.google.com/viewer?url=${file?.file_url}&embedded=true`}
                                                                        title={file.file_category_name}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            border: "none",
                                                                        }}
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Stack alignItems="center" spacing={0.75} sx={{ color: QR.slate }}>
                                                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, opacity: 0.6 }} />
                                                                <Typography sx={{ fontSize: 13 }}>No preview available</Typography>
                                                            </Stack>
                                                        )}
                                                    </Box>

                                                    {/* Footer: type chip + category + download */}
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        spacing={1}
                                                        sx={{ px: 1.5, py: 1.25 }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: 0.5,
                                                                    px: 1,
                                                                    height: 26,
                                                                    flexShrink: 0,
                                                                    borderRadius: 1.5,
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    color: meta.color,
                                                                    bgcolor: `${meta.color}14`,
                                                                }}
                                                            >
                                                                {meta.icon}
                                                                {meta.label}
                                                            </Box>
                                                            <Typography
                                                                noWrap
                                                                title={file.file_category_name}
                                                                sx={{ fontSize: 13.5, fontWeight: 600, color: "#334155", minWidth: 0 }}
                                                            >
                                                                {file.file_category_name}
                                                            </Typography>
                                                        </Stack>
                                                        <Box sx={{ flexShrink: 0, "& .MuiButton-root": { height: 36, px: 1.25, fontSize: 13.5 } }}>
                                                            <QrActionButton
                                                                icon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                                                label="Download"
                                                                variant="soft"
                                                                color={QR.main}
                                                                onClick={() => DownloadFile(file.file_url, file.file_type)}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </>
                    )
                }
            </Box>
        </Box>
    );
}
